/**
 * Shared domain model + API contract. Used by both the server (validation) and the client (types).
 * zod schemas are the source of truth; TS types are inferred at the bottom of the file.
 *
 * Conventions:
 *  - Weights are grams (number, may be fractional). Times are seconds unless suffixed Ms.
 *  - Timestamps are ISO-8601 strings (UTC). Calendar dates (roast date etc.) are 'YYYY-MM-DD'.
 *  - JSON field names are camelCase; DB columns are snake_case (mapping lives in the server repos).
 */
import { z } from 'zod';
import { AI_PROVIDER_IDS } from './ai.js';
import { isValidTimeZone } from './zone.js';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
const isoDateTime = z.string().min(1);
const grams = z.number().finite();
const id = z.number().int().positive();
/** The longest pre-infusion a brew or method takes, in seconds (lever and flow-profiling machines bloom for 20–30 s). */
export const PREINFUSION_MAX_S = 60;
const preinfusionS = z.number().min(0).max(PREINFUSION_MAX_S);

// ---------------------------------------------------------------------------
// Beans
// ---------------------------------------------------------------------------
export const ROAST_LEVELS = ['light', 'medium-light', 'medium', 'medium-dark', 'dark'] as const;
export const RoastLevelSchema = z.enum(ROAST_LEVELS);

export const BeanSchema = z.object({
  id,
  name: z.string().min(1).max(200),
  roaster: z.string().max(200).nullable(),
  origin: z.string().max(200).nullable(),
  variety: z.string().max(200).nullable(),
  process: z.string().max(200).nullable(),
  roastLevel: RoastLevelSchema.nullable(),
  /** Decaffeinated beans; the app marks them with a moon next to the name. */
  decaf: z.boolean(),
  /** How a decaf was decaffeinated (e.g. "Swiss Water", "Sugarcane (EA)"), separate from the process at origin. */
  decafMethod: z.string().max(200).nullable(),
  roastDate: isoDate.nullable(),
  /** Null while the bag is sealed. The first brew with it fills in that brew's day when it's empty (repos/brews.ts). */
  openedDate: isoDate.nullable(),
  /** Initial bag weight in grams. Remaining = bagWeightG - usedG + adjustmentG. */
  bagWeightG: grams.nonnegative().nullable(),
  /**
   * Stock correction in grams (signed), set by "Correct stock" so spills, shared bags or unlogged brews don't
   * have to rewrite the bag weight. 0 = no correction.
   */
  adjustmentG: grams,
  price: z.number().nonnegative().nullable(),
  flavorNotes: z.array(z.string().max(100)).max(50),
  notes: z.string().max(5000).nullable(),
  /** 0-5 in 0.5 steps */
  rating: z.number().min(0).max(5).nullable(),
  archived: z.boolean(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});

export const BeanWithStatsSchema = BeanSchema.extend({
  /** Sum of doseG over brews with this bean (falls back to groundsG when doseG is null). */
  usedG: z.number(),
  /** bagWeightG - usedG + adjustmentG, or null when bagWeightG is null. May go negative. */
  remainingG: z.number().nullable(),
  brewCount: z.number().int(),
  lastBrewAt: isoDateTime.nullable(),
  avgRating: z.number().nullable(),
  /**
   * When the bag photo was last set, or null without one. The image is served at GET /api/beans/:id/photo, set with
   * PUT on the same path (multipart: photo, optional thumb; or JSON { fromBeanId } to copy) and removed with DELETE;
   * see server/routes/beanPhotos.ts, and beanPhotoUrl() on the client.
   */
  photoUpdatedAt: isoDateTime.nullable(),
});

/**
 * Bag photos. The client downsizes them before upload (lib/photo.ts): a JPEG of a few hundred KB, or a WebP or PNG
 * when the image has transparency.
 */
export const BEAN_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type BeanPhotoType = (typeof BEAN_PHOTO_TYPES)[number];
/** Below a Durable Object's 2 MB limit on a SQLite row (hosted edition); the same in both editions. */
export const BEAN_PHOTO_MAX_BYTES = 1536 * 1024;
/** The small square the app shows on cards and beside titles (the client sends one with each photo, a few KB). */
export const BEAN_PHOTO_THUMB_MAX_BYTES = 64 * 1024;

/**
 * A bag weight is required from the API (stock, low-stock warnings and cost per cup hang off it). BeanSchema keeps
 * it nullable for rows and backups from before that rule.
 */
const bagWeight = grams.positive();
export const BeanCreateSchema = BeanSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial().extend({
  name: z.string().min(1).max(200),
  bagWeightG: bagWeight,
});
export const BeanUpdateSchema = BeanSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial().extend({
  bagWeightG: bagWeight.optional(),
});
/**
 * A bean deleted a moment ago, as GET /api/beans/:id gave it (its stats are ignored), and the brews it had: the app's
 * Undo puts it back.
 */
export const BeanRestoreSchema = BeanSchema.omit({ id: true }).extend({
  brewIds: z.array(id).max(10_000),
});

// ---------------------------------------------------------------------------
// Grinders
// ---------------------------------------------------------------------------
export const GrinderSchema = z.object({
  id,
  name: z.string().min(1).max(200),
  burrs: z.string().max(200).nullable(),
  /** Free text unit label shown next to the setting, e.g. "clicks", "dial". */
  settingUnit: z.string().max(50).nullable(),
  /** Optional numeric range for a stepper UI. Settings are still stored as text (e.g. "2.3.1" for 3-level dials). */
  settingMin: z.number().nullable(),
  settingMax: z.number().nullable(),
  settingStep: z.number().positive().nullable(),
  notes: z.string().max(5000).nullable(),
  archived: z.boolean(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});

export const GrinderWithStatsSchema = GrinderSchema.extend({
  brewCount: z.number().int(),
  lastSetting: z.string().nullable(),
  lastUsedAt: isoDateTime.nullable(),
});

export const GrinderCreateSchema = GrinderSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial().extend({
  name: z.string().min(1).max(200),
});
export const GrinderUpdateSchema = GrinderSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial();

// ---------------------------------------------------------------------------
// Vessels: anything put on the scale whose empty weight we know (grinder, dosing cup, portafilter, cup...).
// Used by the smart workflow to recognise what was placed on the scale and auto-tare.
// ---------------------------------------------------------------------------
export const VESSEL_KINDS = ['grinder', 'dosing_cup', 'portafilter', 'brewer', 'cup', 'carafe', 'pitcher', 'other'] as const;
export const VesselKindSchema = z.enum(VESSEL_KINDS);

export const VesselSchema = z.object({
  id,
  name: z.string().min(1).max(200),
  kind: VesselKindSchema,
  /** Empty weight in grams. */
  weightG: grams.positive(),
  /** Match tolerance (+/- grams) used for auto-detection. */
  toleranceG: z.number().positive().max(100),
  /** Optional link: the grinder this vessel belongs to (the grinder itself, or its dosing cup). */
  grinderId: id.nullable(),
  notes: z.string().max(5000).nullable(),
  archived: z.boolean(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export const VesselCreateSchema = VesselSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial().extend({
  name: z.string().min(1).max(200),
  kind: VesselKindSchema,
  weightG: grams.positive(),
});
export const VesselUpdateSchema = VesselSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial();

// ---------------------------------------------------------------------------
// Brew methods + workflow templates
// ---------------------------------------------------------------------------
export const METHOD_KINDS = ['espresso', 'pourover', 'immersion', 'aeropress', 'moka', 'coldbrew', 'other'] as const;
export const MethodKindSchema = z.enum(METHOD_KINDS);

/**
 * One step of the guided smart workflow.
 *  - 'dose':    measure whole beans (this is what's deducted from the bean bag)
 *  - 'grounds': measure ground coffee (e.g. in the portafilter)
 *  - 'brew':    timed + graphed extraction; the measured value is the yield (beverage, or water for pour-over)
 * Each step: wait for empty scale -> wait for a vessel to be placed (stable) -> identify vessel -> tare ->
 * measure content -> step completes when the vessel is lifted off (or the user presses Next).
 * Steps with startInPlace skip the empty/place phases and tare whatever is already on the scale.
 */
export const WORKFLOW_STEP_KINDS = ['dose', 'grounds', 'brew'] as const;
export const WorkflowStepKindSchema = z.enum(WORKFLOW_STEP_KINDS);

export const WorkflowStepSchema = z.object({
  /** Stable id within the method (e.g. 'dose', 'grounds', 'brew' or a random short id). */
  id: z.string().min(1).max(50),
  kind: WorkflowStepKindSchema,
  label: z.string().min(1).max(100),
  /** Which kinds of vessel are expected for this step (used to prefer matches). Empty = any. */
  vesselKinds: z.array(VesselKindSchema),
  /** Preferred specific vessel; null = auto-detect among vesselKinds. */
  vesselId: id.nullable(),
  /** Auto-tare when the expected vessel is detected. */
  autoTare: z.boolean(),
  /** If true, the user can skip this step (e.g. grounds measurement). */
  optional: z.boolean(),
  /**
   * Start this step without clearing the scale: whatever is on the scale stays and is tared in place
   * (e.g. pour-over: brewer stays on the scale after adding grounds, then tare and pour water).
   */
  startInPlace: z.boolean(),
});

export const BrewMethodSchema = z.object({
  id,
  name: z.string().min(1).max(200),
  kind: MethodKindSchema,
  defaultDoseG: grams.positive().nullable(),
  /** yield / dose, e.g. 2.0 for espresso, 16 for pour-over */
  defaultRatio: z.number().positive().nullable(),
  defaultWaterTempC: z.number().min(0).max(100).nullable(),
  /** The pre-infusion the next shot starts from, in seconds (see BrewSchema.preinfusionS). */
  defaultPreinfusionS: preinfusionS.nullable(),
  workflow: z.array(WorkflowStepSchema).max(10),
  notes: z.string().max(5000).nullable(),
  archived: z.boolean(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export const BrewMethodCreateSchema = BrewMethodSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial().extend({
  name: z.string().min(1).max(200),
  kind: MethodKindSchema,
});
export const BrewMethodUpdateSchema = BrewMethodSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial();

// ---------------------------------------------------------------------------
// Brews
// ---------------------------------------------------------------------------
/**
 * What stopped a brew's timer: auto-stop (the flow stayed low after the drip), the yield target (stopAtTargetYield),
 * Stop tapped, the flow moving on to the next step (Next tapped, or milk poured onto the cup) or the cup lifted.
 */
export const STOP_REASONS = ['auto_stop', 'target', 'stop_tapped', 'next', 'cup_lifted'] as const;
export const StopReasonSchema = z.enum(STOP_REASONS);

/**
 * What stopped the pump on a machine with a Manetta board, as the machine reported it (Manetta's StopReason): Taretto
 * (`app`: the cut-off, the target, Stop or Exit), the machine's own controls or programmed volume (`machine`), the
 * shot's time limit as Taretto asked for it (`command_cap`) or the machine's own (`device_cap`), the connection
 * dropping (`link_lost`) or going quiet (`heartbeat`), or a fault.
 */
export const MACHINE_STOP_REASONS = ['app', 'machine', 'command_cap', 'device_cap', 'link_lost', 'heartbeat', 'fault'] as const;
export const MachineStopReasonSchema = z.enum(MACHINE_STOP_REASONS);

/** Compact sample tuple: [elapsed ms since brew timer start, weight g, flow g/s] */
export const BrewSampleSchema = z.tuple([z.number(), z.number(), z.number()]);

export const BrewSchema = z.object({
  id,
  beanId: id.nullable(),
  grinderId: id.nullable(),
  methodId: id.nullable(),
  grindSetting: z.string().max(100).nullable(),
  /** Whole beans weighed (deducted from the bag). */
  doseG: grams.nonnegative().nullable(),
  /** Ground coffee measured (e.g. in portafilter). */
  groundsG: grams.nonnegative().nullable(),
  /** Beverage weight (espresso) or water poured (pour-over). */
  yieldG: grams.nonnegative().nullable(),
  brewTimeS: z.number().nonnegative().nullable(),
  /** Seconds from timer start until first liquid registered on the scale. */
  firstDropS: z.number().nonnegative().nullable(),
  /**
   * Seconds from the timer start (like brewTimeS) to when the live cue said to stop the pump, recorded by the app.
   * null when no cue showed: not a shot, no target, the cue switched off, or a shot that ended before it was due.
   */
  cutoffCueS: z.number().nonnegative().nullable(),
  /**
   * What stopped the timer (STOP_REASONS), recorded by the app. null when it never ran (a brew logged by hand, or one
   * finished before the timer started). Before auto_stop the curve runs on through the drip; target, stop_tapped and
   * next can end it before the pump stopped.
   */
  stopReason: StopReasonSchema.nullable(),
  /**
   * What stopped the pump (MACHINE_STOP_REASONS), as a connected machine with a Manetta board reported it. null
   * without one, or when it never said (the connection was gone until the brew was saved).
   */
  machineStopReason: MachineStopReasonSchema.nullable(),
  /**
   * When the machine said the shot stopped, seconds from the timer start (like brewTimeS): its own record of the
   * stop (Status's elapsed_ms), as a connected machine with a Manetta board reported it. null without one, for a
   * shot already running before the brew step waited (its timer didn't start with it), or when it never said.
   */
  machineStopS: z.number().nonnegative().nullable(),
  waterTempC: z.number().min(0).max(100).nullable(),
  /**
   * The machine's pre-infusion setting in seconds, entered by the user (nothing measures it). 0 = no pre-infusion,
   * null = not recorded. A manual Start puts the pre-infusion inside firstDropS and brewTimeS; an auto-start doesn't.
   */
  preinfusionS: preinfusionS.nullable(),
  /** The dose the recipe asked for (doseG is what was actually weighed). */
  targetDoseG: grams.nonnegative().nullable(),
  targetYieldG: grams.nonnegative().nullable(),
  /** Milk drink this shot went into: the drink preset's name at brew time. null = straight (no milk). */
  drink: z.string().max(100).nullable(),
  /** The milk used, free text (e.g. "Oatly Barista"). */
  milk: z.string().max(100).nullable(),
  /** Cold milk the recipe asked for. */
  targetMilkG: grams.nonnegative().nullable(),
  /** Cold milk weighed into the pitcher before steaming. */
  milkDoseG: grams.nonnegative().nullable(),
  /** Milk that ended up in the drink (drink weight - cup - shot). Includes the water the steam condensed into it. */
  milkPouredG: grams.nonnegative().nullable(),
  milkTempC: z.number().min(0).max(100).nullable(),
  /** 0-5 in 0.5 steps */
  rating: z.number().min(0).max(5).nullable(),
  tastingNotes: z.array(z.string().max(100)).max(50),
  notes: z.string().max(5000).nullable(),
  samples: z.array(BrewSampleSchema).max(20000),
  brewedAt: isoDateTime,
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});

/** List/detail representation: samples are omitted from lists; joined names are included. */
export const BrewListItemSchema = BrewSchema.omit({ samples: true }).extend({
  beanName: z.string().nullable(),
  /** The bean's decaf flag; null without a bean. */
  beanDecaf: z.boolean().nullable(),
  roaster: z.string().nullable(),
  grinderName: z.string().nullable(),
  methodName: z.string().nullable(),
  methodKind: MethodKindSchema.nullable(),
  sampleCount: z.number().int(),
});
export const BrewDetailSchema = BrewListItemSchema.extend({
  samples: z.array(BrewSampleSchema),
});

export const BrewCreateSchema = BrewSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial().extend({
  brewedAt: isoDateTime.optional(),
});
export const BrewUpdateSchema = BrewSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial();
/** A brew deleted a moment ago, as GET /api/brews/:id gave it (its names are ignored): the app's Undo puts it back. */
export const BrewRestoreSchema = BrewSchema.omit({ id: true });

export const BrewListQuerySchema = z.object({
  beanId: z.coerce.number().int().positive().optional(),
  grinderId: z.coerce.number().int().positive().optional(),
  methodId: z.coerce.number().int().positive().optional(),
  minRating: z.coerce.number().min(0).max(5).optional(),
  /** Words that must all appear in the bean, roaster, method, grinder, drink, milk, notes or tasting notes. */
  q: z.string().trim().max(200).optional(),
  /** The grind setting, as written (case and surrounding spaces aside). */
  grind: z.string().trim().max(40).optional(),
  /** First and last day to include, both inclusive, as days in the user's time zone (settings.timeZone). */
  since: isoDate.optional(),
  until: isoDate.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export const BrewListResponseSchema = z.object({
  items: z.array(BrewListItemSchema),
  total: z.number().int(),
});

/** GET /api/suggestions/last-brew?beanId&grinderId&methodId */
export const LastBrewSuggestionSchema = z.object({
  /**
   * Best matching previous brew: bean+grinder+method > grinder+method > bean+grinder > grinder > bean.
   * When a method is given, the levels without it only use brews of the same method kind (or with no method), so a
   * pour-over is never prefilled from, or hinted at with, an espresso shot.
   */
  brew: BrewListItemSchema.nullable(),
  /** How the brew was matched. */
  matchedOn: z.enum(['bean+grinder+method', 'bean+grinder', 'grinder+method', 'grinder', 'bean', 'none']),
  grindSetting: z.string().nullable(),
  doseG: z.number().nullable(),
  yieldG: z.number().nullable(),
});

// ---------------------------------------------------------------------------
// Stats (dashboard)
// ---------------------------------------------------------------------------
export const StatsSchema = z.object({
  totalBrews: z.number().int(),
  brewsToday: z.number().int(),
  brewsLast7Days: z.number().int(),
  activeBeans: z.number().int(),
  totalUsedG: z.number(),
  usedLast7DaysG: z.number(),
  avgRatingLast30Days: z.number().nullable(),
  /** Daily brew counts for the last 14 days, oldest first. date = YYYY-MM-DD (server local time). */
  daily: z.array(z.object({ date: isoDate, brews: z.number().int(), usedG: z.number() })),
  /** Active (non-archived) beans with remainingG below lowStockThresholdG. */
  lowStock: z.array(z.object({ id, name: z.string(), decaf: z.boolean(), remainingG: z.number() })),
});

// ---------------------------------------------------------------------------
// Milk drinks
// ---------------------------------------------------------------------------
/** A ratio drink's milk without a target yield is worked out for a typical double. */
export const MILK_RATIO_FALLBACK_YIELD_G = 36;
export const MILK_RATIO_MIN = 0.5;
export const MILK_RATIO_MAX = 15;

/** The grams stored with a ratio drink: for a recipe without a target yield, and for builds before ratio drinks. */
export function ratioFallbackMilkG(milkRatio: number): number {
  return Math.round(milkRatio * MILK_RATIO_FALLBACK_YIELD_G);
}

/**
 * A milk drink offered at setup (espresso only). Its milk is the cold milk to weigh into the pitcher, not what ends up
 * in the cup: fixed grams, or with milkRatio, that many grams per gram of the shot's target yield.
 */
export const DrinkPresetSchema = z.object({
  id: z.string().min(1).max(50),
  name: z.string().trim().min(1).max(50),
  /** Fixed drinks: the cold milk. Ratio drinks: the fallback, ratioFallbackMilkG(milkRatio). */
  milkG: grams.positive().max(2000),
  /** Cold milk per gram of target yield: 3.5 = 1:3.5. Unset = fixed grams. */
  milkRatio: z.number().min(MILK_RATIO_MIN).max(MILK_RATIO_MAX).optional(),
});

const ratioDrink = (id: string, name: string, milkRatio: number) => ({ id, name, milkG: ratioFallbackMilkG(milkRatio), milkRatio });

/**
 * The usual espresso:milk ratios (a cortado is equal parts), as starting points to tune by brewing. The cold milk is
 * close to the milk in the cup: steaming adds a little water, and a little stays in the pitcher.
 */
export const DEFAULT_DRINKS: readonly z.infer<typeof DrinkPresetSchema>[] = [
  ratioDrink('cortado', 'Cortado', 1),
  ratioDrink('flat-white', 'Flat white', 2),
  ratioDrink('cappuccino', 'Cappuccino', 3),
  ratioDrink('latte', 'Latte', 5),
];

/**
 * Where a drink's milk target comes from: fixed grams, the ratio times the target yield, or (a ratio drink without a
 * target yield) the stored fallback grams.
 */
export type DrinkMilkTarget =
  | { kind: 'fixed'; milkG: number }
  | { kind: 'ratio'; milkG: number; milkRatio: number; yieldG: number }
  | { kind: 'fallback'; milkG: number; milkRatio: number };

/** The cold milk a preset asks for with this target yield; a ratio drink's is rounded to whole grams. */
export function drinkMilkTarget(preset: Pick<z.infer<typeof DrinkPresetSchema>, 'milkG' | 'milkRatio'>, targetYieldG: number | null | undefined): DrinkMilkTarget {
  const { milkRatio } = preset;
  if (milkRatio == null) return { kind: 'fixed', milkG: preset.milkG };
  if (targetYieldG != null && Number.isFinite(targetYieldG) && targetYieldG > 0) {
    return { kind: 'ratio', milkG: Math.round(targetYieldG * milkRatio), milkRatio, yieldG: targetYieldG };
  }
  return { kind: 'fallback', milkG: preset.milkG, milkRatio };
}

/** GET /api/suggestions/milk?methodId */
export const MilkSuggestionSchema = z.object({
  /** The latest brew with that method (any bean or grinder); drink null = straight. Null without one, or without methodId. */
  last: z.object({ brewId: id, drink: z.string().nullable(), targetMilkG: z.number().nullable() }).nullable(),
  /** Milks used before, most recently used first (max 20). The first one prefills the next drink. */
  milks: z.array(z.string()),
});

// ---------------------------------------------------------------------------
// Settings (stored server-side so they sync across devices)
// ---------------------------------------------------------------------------
export const SettingsSchema = z.object({
  /** Readings must stay within +/- stabilityToleranceG for stabilityWindowMs to count as stable. */
  stabilityWindowMs: z.number().int().min(200).max(5000).default(800),
  stabilityToleranceG: z.number().min(0.05).max(5).default(0.3),
  /** |weight| below this counts as "empty scale". */
  emptyThresholdG: z.number().min(0.1).max(20).default(1.5),
  /** Default +/- tolerance for vessel matching (a vessel's own toleranceG wins). */
  vesselToleranceG: z.number().min(0.5).max(50).default(3),
  /** Minimum weight of an unknown object before we offer to tare it. */
  minVesselWeightG: z.number().min(1).max(500).default(20),
  /** Auto start the brew timer when weight rises above this during the brew step. */
  flowStartThresholdG: z.number().min(0.1).max(10).default(0.5),
  autoStartTimer: z.boolean().default(true),
  /** Auto stop the brew when flow stays below autoStopFlowGs for autoStopDelayS. */
  autoStopBrew: z.boolean().default(true),
  autoStopFlowGs: z.number().min(0).max(2).default(0.15),
  autoStopDelayS: z.number().min(1).max(30).default(4),
  /**
   * Also stop the timer when the yield target is reached. On a shot that ends the curve at the target, before the
   * pump stops, so the cut-off cue can't learn the drip from it.
   */
  stopAtTargetYield: z.boolean().default(false),
  /**
   * On a shot with a target, say when to stop the pump so the cup ends on the target after the drip (a countdown, a
   * "Stop the pump" card and a beep). Learns the drip and the lag per method (GET /api/suggestions/cutoff).
   */
  cutoffCue: z.boolean().default(true),
  /** Send tare/timer commands to the physical scale (otherwise tares are done in software only). */
  useScaleTare: z.boolean().default(true),
  lowStockThresholdG: z.number().min(0).max(1000).default(60),
  defaultMethodId: id.nullable().default(null),
  autoReconnectScale: z.boolean().default(true),
  /**
   * Ping the scale so it doesn't auto-power-off while the app is using it (pings stop when idle).
   * auto = only the Themis Ultra, which documents the keep-awake command.
   */
  keepAwakeScale: z.enum(['auto', 'always', 'never']).default('auto'),
  /**
   * Switch the scale off once a brew is saved (the review step can change it per brew). Only scales whose driver
   * can (the Themis Ultra, fw >= 4.0.0, not while charging); others keep their own auto-off.
   */
  powerOffScaleAfterSave: z.boolean().default(false),
  /**
   * Turn the coffee machine off once a brew is saved (the review step can change it per brew). Only machines whose
   * Manetta board can turn them off; turning off also ends steam mode.
   */
  powerOffMachineAfterSave: z.boolean().default(false),
  /**
   * Let Taretto stop the brew: a connected machine whose Manetta board can start and stop a shot stops it at the
   * cut-off cue's moment (or at the target with stopAtTargetYield). Off: Taretto still starts it, and the barista
   * stops it.
   */
  machineStopsShot: z.boolean().default(true),
  /**
   * Switch a connected machine whose Manetta board can (steam_mode) to steam once a milk drink's shot is over, and back
   * to brew heat once the milk is in the drink. Only with the milk after the shot (not milkFirst: the machine won't
   * brew in steam mode). Each brew can switch it in setup.
   */
  machineSteamAfterShot: z.boolean().default(true),
  /** Smooth the flow graph using a moving average over this many seconds. */
  flowSmoothingS: z.number().min(0).max(5).default(1),
  /** Milk drinks offered at setup for espresso. A function default, so no parse shares the array. */
  drinks: z
    .array(DrinkPresetSchema)
    .max(12)
    .refine((d) => new Set(d.map((x) => x.name.toLowerCase())).size === d.length, 'Drink names must be different')
    .default(() => DEFAULT_DRINKS.map((d) => ({ ...d }))),
  /** Weigh the milk before the shot (steaming before or during it) instead of after. Each brew can switch it in setup. */
  milkFirst: z.boolean().default(false),
  /** Show and enter temperatures in °C or °F. Display only: temperatures are always stored and sent as °C. */
  temperatureUnit: z.enum(['c', 'f']).default('c'),
  /**
   * Show and enter bag sizes, bean stock, the low-stock threshold and milk amounts in grams or (weight) ounces. Display
   * only: weights are always stored and sent as grams, and dose, yield and flow are always shown in grams.
   */
  weightUnit: z.enum(['g', 'oz']).default('g'),
  /**
   * IANA time zone the server counts calendar days in (brews today, the daily chart, days off roast). null = the
   * server's own (TZ in Docker, UTC when hosted). The app fills it in from the browser.
   */
  timeZone: z.string().max(64).refine(isValidTimeZone, 'Unknown time zone').nullable().default(null),
});
export const SettingsUpdateSchema = SettingsSchema.partial();

// ---------------------------------------------------------------------------
// Ask Taretto's chats: on a self-hosted server for every device; Taretto Cloud keeps them on each device instead
// ---------------------------------------------------------------------------
/** Past this, the chat asked in longest ago is forgotten. */
export const MAX_CHATS = 20;

/** The brew or bean a question was asked about, from that one's page. */
export const ChatContextSchema = z.object({
  kind: z.enum(['brew', 'bean']),
  id,
  /** How the app names it ("Kenya AA, 3 Oct 08:14"). */
  label: z.string().max(300),
});

/** A brew an answer's "Brews:" line links to, as a tool returned it (client/ai/citations.ts). */
export const ChatCitedBrewSchema = z.object({
  id,
  beanName: z.string().max(300).nullable(),
  brewedAt: isoDateTime,
  rating: z.number().nullable(),
});

/**
 * One question and what came back, as the app shows it: no provider's messages, so a chat continued elsewhere sends the
 * earlier questions and answers as text and the model reads again what it needs.
 */
export const ChatExchangeCreateSchema = z.object({
  question: z.string().min(1).max(4000),
  context: ChatContextSchema.nullable().default(null),
  /** As far as it got, when it was stopped or failed. */
  answer: z.string().max(100_000),
  /** What the answer read ("Read 8 Kenya AA brews"). */
  reads: z.array(z.string().max(300)).max(100).default([]),
  /** The brews its "Brews:" line names, in its order. */
  brews: z.array(ChatCitedBrewSchema).max(20).default([]),
  /** Who answered: a provider, or the simulator a dev build answers with (client/ai/simulator.ts). */
  provider: z.enum([...AI_PROVIDER_IDS, 'simulator']),
  model: z.string().max(256),
  stopped: z.boolean().default(false),
  /** Why it failed, in words. */
  error: z.string().max(2000).nullable().default(null),
  /** The fix for the failure is in Settings › AI (a wrong key or model). */
  fixInSettings: z.boolean().default(false),
  /** When it was asked; now when left out. */
  askedAt: isoDateTime.optional(),
});
export const ChatExchangeSchema = ChatExchangeCreateSchema.extend({ id, askedAt: isoDateTime });

/** A chat with its questions, oldest first. */
export const ChatSchema = z.object({ id, exchanges: z.array(ChatExchangeSchema) });

/** A chat in the list: by its first question, when it was last asked in and how many questions it has. */
export const ChatSummarySchema = z.object({ id, title: z.string(), askedAt: isoDateTime, questions: z.number().int() });

export const ChatCreateSchema = z.object({ exchanges: z.array(ChatExchangeCreateSchema).min(1).max(500) });

/** The next question, or with `replaces` one asked again in place of that one (Try again). */
export const ChatAppendSchema = ChatExchangeCreateSchema.extend({ replaces: id.optional() });

// ---------------------------------------------------------------------------
// Backup
// ---------------------------------------------------------------------------
export const BeanPhotoBackupSchema = z.object({
  beanId: id,
  mime: z.enum(BEAN_PHOTO_TYPES),
  /** Base64 image bytes. */
  data: z.string().min(1),
  /** Base64 thumbnail, or null without one. */
  thumb: z.string().min(1).nullable().default(null),
  updatedAt: isoDateTime,
});

/** A bag photo in a backup archive's photos.json (backupArchive.ts): its bytes are files beside it. */
export const BackupPhotoFileSchema = z.object({
  beanId: id,
  mime: z.enum(BEAN_PHOTO_TYPES),
  /** The photo's path in the archive, e.g. photos/12-ethiopia-guji.jpg. */
  file: z.string().min(1),
  /** Its thumbnail's path, or null without one. */
  thumbFile: z.string().min(1).nullable().default(null),
  updatedAt: isoDateTime,
});
export const BackupPhotosSchema = z.object({ app: z.literal('taretto'), photos: z.array(BackupPhotoFileSchema) });

// Fields added after the first release default here, so older backups still restore.
export const BackupSchema = z.object({
  app: z.literal('taretto'),
  version: z.number().int(),
  exportedAt: isoDateTime,
  beans: z.array(BeanSchema.extend({ adjustmentG: BeanSchema.shape.adjustmentG.default(0), decaf: BeanSchema.shape.decaf.default(false), decafMethod: BeanSchema.shape.decafMethod.default(null) })),
  grinders: z.array(GrinderSchema),
  vessels: z.array(VesselSchema),
  methods: z.array(BrewMethodSchema.extend({ defaultPreinfusionS: BrewMethodSchema.shape.defaultPreinfusionS.default(null) })),
  brews: z.array(
    BrewSchema.extend({
      preinfusionS: BrewSchema.shape.preinfusionS.default(null),
      cutoffCueS: BrewSchema.shape.cutoffCueS.default(null),
      stopReason: BrewSchema.shape.stopReason.default(null),
      machineStopReason: BrewSchema.shape.machineStopReason.default(null),
      machineStopS: BrewSchema.shape.machineStopS.default(null),
      targetDoseG: BrewSchema.shape.targetDoseG.default(null),
      drink: BrewSchema.shape.drink.default(null),
      milk: BrewSchema.shape.milk.default(null),
      targetMilkG: BrewSchema.shape.targetMilkG.default(null),
      milkDoseG: BrewSchema.shape.milkDoseG.default(null),
      milkPouredG: BrewSchema.shape.milkPouredG.default(null),
      milkTempC: BrewSchema.shape.milkTempC.default(null),
    }),
  ),
  beanPhotos: z.array(BeanPhotoBackupSchema).default([]),
  settings: SettingsSchema,
  /** Ask Taretto's chats, from a self-hosted server. Taretto Cloud keeps none, so it leaves them out of a restore. */
  chats: z.array(ChatSchema).default([]),
});

export const ServerInfoSchema = z.object({
  name: z.literal('taretto'),
  version: z.string(),
  /** The full git commit the server was built from; null when the build wasn't given one (local builds). Missing on servers older than this change. */
  commit: z.string().nullable().optional(),
  /** Missing on servers older than 0.3.0, which are all self-hosted. */
  edition: z.enum(['self-hosted', 'hosted']).optional(),
  /** Hosted only: who is signed in, or null when nobody is (the app then shows the sign-in screen). */
  account: z.object({ email: z.string().nullable() }).nullable().optional(),
  limits: z
    .object({
      /** The largest backup this server restores, in bytes. */
      backupBytes: z.number().int(),
      /**
       * The most JSON one restore parses: a .zip's taretto.json once unzipped, or a JSON backup. Parsing takes many
       * times its size in memory. Missing on servers older than this change, which took as much as backupBytes.
       */
      backupJsonBytes: z.number().int().optional(),
    })
    .optional(),
  /**
   * The AI the server was started with (AI_PROVIDER, AI_MODEL): every device's suggestions go through it, with its
   * key, which never leaves the server. Null on Taretto Cloud and without AI_* set. Missing on servers older than this.
   * `host` names the OpenAI-compatible server it forwards to with AI_BASE_URL (ollama:11434), and is missing without.
   */
  ai: z.object({ provider: z.enum(AI_PROVIDER_IDS), model: z.string(), host: z.string().optional() }).nullable().optional(),
  /**
   * Whether this server collects usage data (docs/usage.md): route counts, and the events of accounts that said yes.
   * Only then does the app ask, and send anything. Missing on servers older than this, and on Self-hosted for now.
   */
  usage: z.boolean().optional(),
  tls: z.object({
    mode: z.enum(['auto', 'files', 'off']),
    httpsPort: z.number().int().nullable(),
    caAvailable: z.boolean(),
    /** Common name of the local CA (what iOS lists under Certificate Trust Settings); null without one. */
    caName: z.string().nullable(),
    hostnames: z.array(z.string()),
  }),
});

export const ApiErrorSchema = z.object({
  error: z.string(),
  details: z.unknown().optional(),
});

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------
export type RoastLevel = z.infer<typeof RoastLevelSchema>;
export type Bean = z.infer<typeof BeanSchema>;
export type BeanWithStats = z.infer<typeof BeanWithStatsSchema>;
export type BeanCreate = z.infer<typeof BeanCreateSchema>;
export type BeanUpdate = z.infer<typeof BeanUpdateSchema>;
export type BeanRestore = z.infer<typeof BeanRestoreSchema>;

export type Grinder = z.infer<typeof GrinderSchema>;
export type GrinderWithStats = z.infer<typeof GrinderWithStatsSchema>;
export type GrinderCreate = z.infer<typeof GrinderCreateSchema>;
export type GrinderUpdate = z.infer<typeof GrinderUpdateSchema>;

export type VesselKind = z.infer<typeof VesselKindSchema>;
export type Vessel = z.infer<typeof VesselSchema>;
export type VesselCreate = z.infer<typeof VesselCreateSchema>;
export type VesselUpdate = z.infer<typeof VesselUpdateSchema>;

export type MethodKind = z.infer<typeof MethodKindSchema>;
export type WorkflowStepKind = z.infer<typeof WorkflowStepKindSchema>;
export type WorkflowStep = z.infer<typeof WorkflowStepSchema>;
export type BrewMethod = z.infer<typeof BrewMethodSchema>;
export type BrewMethodCreate = z.infer<typeof BrewMethodCreateSchema>;
export type BrewMethodUpdate = z.infer<typeof BrewMethodUpdateSchema>;

export type BrewSample = z.infer<typeof BrewSampleSchema>;
export type StopReason = z.infer<typeof StopReasonSchema>;
export type MachineStopReason = z.infer<typeof MachineStopReasonSchema>;
export type Brew = z.infer<typeof BrewSchema>;
export type BrewListItem = z.infer<typeof BrewListItemSchema>;
export type BrewDetail = z.infer<typeof BrewDetailSchema>;
export type BrewCreate = z.infer<typeof BrewCreateSchema>;
export type BrewUpdate = z.infer<typeof BrewUpdateSchema>;
export type BrewRestore = z.infer<typeof BrewRestoreSchema>;
export type BrewListQuery = z.input<typeof BrewListQuerySchema>;
export type BrewListResponse = z.infer<typeof BrewListResponseSchema>;
export type LastBrewSuggestion = z.infer<typeof LastBrewSuggestionSchema>;
export type DrinkPreset = z.infer<typeof DrinkPresetSchema>;
export type MilkSuggestion = z.infer<typeof MilkSuggestionSchema>;

export type Stats = z.infer<typeof StatsSchema>;
export type Settings = z.infer<typeof SettingsSchema>;
export type SettingsUpdate = z.infer<typeof SettingsUpdateSchema>;
export type ChatContext = z.infer<typeof ChatContextSchema>;
export type ChatExchangeCreate = z.infer<typeof ChatExchangeCreateSchema>;
export type ChatExchange = z.infer<typeof ChatExchangeSchema>;
export type Chat = z.infer<typeof ChatSchema>;
export type ChatSummary = z.infer<typeof ChatSummarySchema>;
export type ChatAppend = z.infer<typeof ChatAppendSchema>;
export type Backup = z.infer<typeof BackupSchema>;
export type BeanPhotoBackup = z.infer<typeof BeanPhotoBackupSchema>;
export type BackupPhotoFile = z.infer<typeof BackupPhotoFileSchema>;
export type ServerInfo = z.infer<typeof ServerInfoSchema>;
export type ApiError = z.infer<typeof ApiErrorSchema>;

// ---------------------------------------------------------------------------
// Helpers shared by client + server
// ---------------------------------------------------------------------------
export const DEFAULT_SETTINGS: Settings = SettingsSchema.parse({});

/** Brew ratio = yield / input, where input = groundsG ?? doseG. Returns null if not computable. */
export function brewRatio(b: { yieldG: number | null; groundsG: number | null; doseG: number | null }): number | null {
  const input = b.groundsG ?? b.doseG;
  if (b.yieldG == null || input == null || input <= 0) return null;
  return b.yieldG / input;
}

/**
 * The time to first drip that was actually measured, else null. A recorded 0 means the timer started on the flow
 * itself (auto-start, or Start tapped with liquid already running): there's no time to show or compare.
 */
export function measuredFirstDropS(b: { firstDropS: number | null }): number | null {
  return b.firstDropS != null && b.firstDropS > 0 ? b.firstDropS : null;
}

export const VESSEL_KIND_LABELS: Record<VesselKind, string> = {
  grinder: 'Grinder',
  dosing_cup: 'Dosing cup',
  portafilter: 'Portafilter',
  brewer: 'Brewer',
  cup: 'Cup',
  carafe: 'Carafe / server',
  pitcher: 'Milk pitcher',
  other: 'Other',
};

export const METHOD_KIND_LABELS: Record<MethodKind, string> = {
  espresso: 'Espresso',
  pourover: 'Pour-over',
  immersion: 'Immersion',
  aeropress: 'AeroPress',
  moka: 'Moka pot',
  coldbrew: 'Cold brew',
  other: 'Other',
};

/**
 * Method kinds whose brew can go into a milk drink. Only espresso: its yield is always the drink in the cup, so a milk
 * ratio to it means something. An AeroPress's yield is the water poured in, and a full cup at 1:15 would ask for litres.
 */
export const DRINK_METHOD_KINDS: readonly MethodKind[] = ['espresso'];

export function drinksAvailable(kind: MethodKind | null | undefined): boolean {
  return kind != null && DRINK_METHOD_KINDS.includes(kind);
}

/** Method kinds the app offers a pre-infusion field for. The API takes one on any brew or method. */
export const PREINFUSION_METHOD_KINDS: readonly MethodKind[] = ['espresso'];

/** Whether the app shows a pre-infusion field: on espresso, and wherever one is already set. */
export function showsPreinfusion(kind: MethodKind | null | undefined, value: number | null | undefined = null): boolean {
  return value != null || (kind != null && PREINFUSION_METHOD_KINDS.includes(kind));
}

/** The preset with this name (case-insensitive), if any. */
export function findDrinkPreset(drinks: readonly DrinkPreset[], name: string | null | undefined): DrinkPreset | null {
  if (!name) return null;
  const key = name.trim().toLowerCase();
  return drinks.find((d) => d.name.toLowerCase() === key) ?? null;
}

/** Equality for setting values: by value for arrays/objects (drinks), since every parse makes a fresh copy. */
export function sameSettingValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  return typeof a === 'object' && a !== null && typeof b === 'object' && b !== null && JSON.stringify(a) === JSON.stringify(b);
}

/** Default workflows seeded for new installs and offered as templates in the method editor. */
export const DEFAULT_WORKFLOWS: Record<'espresso' | 'pourover', WorkflowStep[]> = {
  espresso: [
    { id: 'dose', kind: 'dose', label: 'Weigh beans', vesselKinds: ['grinder', 'dosing_cup'], vesselId: null, autoTare: true, optional: false, startInPlace: false },
    { id: 'grounds', kind: 'grounds', label: 'Grind into portafilter', vesselKinds: ['portafilter'], vesselId: null, autoTare: true, optional: true, startInPlace: false },
    { id: 'brew', kind: 'brew', label: 'Pull the shot', vesselKinds: ['cup'], vesselId: null, autoTare: true, optional: false, startInPlace: false },
  ],
  pourover: [
    { id: 'dose', kind: 'dose', label: 'Weigh beans', vesselKinds: ['grinder', 'dosing_cup'], vesselId: null, autoTare: true, optional: false, startInPlace: false },
    { id: 'grounds', kind: 'grounds', label: 'Add grounds to brewer', vesselKinds: ['brewer', 'carafe'], vesselId: null, autoTare: true, optional: true, startInPlace: false },
    { id: 'brew', kind: 'brew', label: 'Pour water', vesselKinds: ['brewer', 'carafe'], vesselId: null, autoTare: true, optional: false, startInPlace: true },
  ],
};
